import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, finalize } from 'rxjs';
import { PolicyModel, PolicyToUpdateModel } from 'src/app/models/api/governance/policy.model';
import { MatDialog } from '@angular/material/dialog';
import { AddDomainDialogComponent } from '../add-domain-dialog/add-domain-dialog.component';
import { IntegrationModel } from 'src/app/models/api';
import _ from 'lodash';
import { StatusCodeEnum } from 'src/app/models/api/integration.model';
import { InactiveIntegrationDialogComponent } from '../inactive-integration-dialog/inactive-integration-dialog.component';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { AccountSettingsHttpService } from 'src/app/services/httpServices/governance/accountSettingsHttpService/account-settings-http.service';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { IDomainsViewModel } from 'src/app/models/viewModels/domainsViewModel';
import { IWarningDialogInput, WarningDialogComponent } from '../warning-dialog/warning-dialog.component';
import { EffectivePolicySettingEnum } from 'src/app/services/infra/store/model/effectivePolicySetting.model';

@Component({
  selector: 'app-trusted-domains',
  templateUrl: './trusted-domains.component.html',
  styleUrls: ['./trusted-domains.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
        position: -webkit-sticky;
        position: sticky;
        top: 0px;
      }

      @media screen and (max-width: 64em) {
        :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
          top: 0px;
        }
      }
    `,
  ],
})
export class TrustedDomainsComponent implements OnInit {
  @Input() public policyModel: PolicyModel;
  @Input() public integrationModel: IntegrationModel;
  @Input() public editable = true;
  @Input() public tableNotFoundText = 'No results found';
  @Input() public tableNotFoundIcon = 'assets/images/common/no-results-found.svg';
  @Output() public goBack: EventEmitter<void> = new EventEmitter<void>();
  @Output() public policyUpdated: EventEmitter<PolicyModel> = new EventEmitter<PolicyModel>();
  @Output() public notifyChanges: EventEmitter<boolean> = new EventEmitter<boolean>();

  public EffectivePolicySettingEnum = EffectivePolicySettingEnum;

  // domains list
  public allowedDomainsStartState: string[];
  public domainsForSearchBox: string[] = [];

  // loading behavior subject
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public changesWereDone = false;

  public domainsCurrentPage$: BehaviorSubject<IDomainsViewModel[]> = new BehaviorSubject<IDomainsViewModel[]>([]);
  public resetSearchBoxBehavioralSubject$: ReplaySubject<string[] | undefined> = new ReplaySubject<
    string[] | undefined
  >();

  private readonly destroyRef = inject(DestroyRef);
  private _domainsViewModel: IDomainsViewModel[] = [];

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _accountSettingsHttpService: AccountSettingsHttpService,
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService
  ) {
    // in order to keep the scope of the function for this. inspiration: https://stackoverflow.com/questions/38245450/angular2-components-this-is-undefined-when-executing-callback-function
    this.deleteDomains = this.deleteDomains.bind(this);
  }

  ngOnInit(): void {
    this.allowedDomainsStartState = this.policyModel.config.allowedDomains.slice();
    this._domainsViewModel = this.allowedDomainsStartState.map((domain) => this._transformToDomainViewModel(domain));
    this._updateDomainsViewModel();
  }

  public addDomainClicked() {
    // Pass current domains (including unsaved) to the dialog for conflict detection
    const currentDomains = this._domainsViewModel.map((vm) => vm.rowId);
    const dialogData = {
      ...this.policyModel,
      config: {
        ...this.policyModel.config,
        allowedDomains: currentDomains,
      },
    };

    const dialogRef = this._matDialog.open(AddDomainDialogComponent, {
      data: dialogData,
      disableClose: false,
      autoFocus: false,
      panelClass: 'dynamic-size-popup-dialog-container',
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (result: { newDomains: string[]; domainsToRemove?: string[]; skippedCount: number } | string[] | undefined) => {
          if (result) {
            // Handle both old format (string[]) and new format with skipped count
            let domains: string[];
            let skippedCount = 0;
            let domainsToRemove: string[] = [];

            if (Array.isArray(result)) {
              // Old format - backward compatibility
              domains = result;
            } else if (result.newDomains) {
              // New format with skipped count and domainsToRemove
              domains = result.newDomains;
              skippedCount = result.skippedCount || 0;
              domainsToRemove = result.domainsToRemove || [];
            } else {
              return;
            }

            // Remove domains that are being replaced by wildcards
            if (domainsToRemove.length > 0) {
              const domainsToRemoveLower = domainsToRemove.map((d) => d.toLowerCase());
              this._domainsViewModel = this._domainsViewModel.filter(
                (vm) => !domainsToRemoveLower.includes(vm.rowId.toLowerCase())
              );
            }

            if (domains && domains.length > 0) {
              this._domainsViewModel.push(...domains.map((domain) => this._transformToDomainViewModel(domain)));
              this._updateDomainsViewModel();
              this.changesWereDone = true;
              this.notifyChanges.emit(this.changesWereDone);

              let message = `${domains.length} new domain${domains.length > 1 ? 's' : ''} will be added.`;
              if (domainsToRemove.length > 0) {
                message += ` ${domainsToRemove.length} domain${domainsToRemove.length > 1 ? 's' : ''} will be replaced.`;
              }
              if (skippedCount > 0) {
                message += ` ${skippedCount} domain${skippedCount > 1 ? 's' : ''} were skipped (already exist).`;
              }

              this._notificationService.success(message);
            }
          }
        }
      );
  }

  public resetForm() {
    this._domainsViewModel = this.allowedDomainsStartState.map((domain) => this._transformToDomainViewModel(domain));
    this._updateDomainsViewModel();
    this.resetSearchBoxBehavioralSubject$.next(this.allowedDomainsStartState);
    this.changesWereDone = false;

    this.notifyChanges.emit(this.changesWereDone);
  }

  public deleteDomains(domains: string[]): Observable<string> {
    const domainsAfterDeletion = this.allowedDomainsStartState.filter((domain) => !domains.includes(domain));
    return this._policiesHttpService.updatePolicy(
      this.integrationModel.id,
      this.policyModel.id,
      this._generateUpdatePolicyModel(domainsAfterDeletion)
    );
  }

  public domainSearched(domains: string[]) {
    const domainsViewModel = this._domainsViewModel
      .map((domainViewModel) => this._transformToDomainViewModel(domainViewModel.displayName))
      .filter((domainViewModel) => domains.includes(domainViewModel.displayName))
      .sort((a, b) => a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()));
    this.domainsCurrentPage$.next(domainsViewModel);
  }

  public updatePolicyConfig() {
    if (this.integrationModel.connectionStatus.code == StatusCodeEnum.off) {
      const dialogRef = this._matDialog.open(InactiveIntegrationDialogComponent, {
        data: this.integrationModel,
        disableClose: false,
        panelClass: 'dynamic-size-popup-dialog-container',
      });
      dialogRef
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this._updatePolicyConfig();
        });
    } else {
      this._updatePolicyConfig();
    }
  }

  public cancelSettings() {
    this.goBack.emit();
  }

  public removeDomainClicked(domainToRemove: IDomainsViewModel): void {
    const dialogRef = this._matDialog.open(WarningDialogComponent, {
      data: {
        elementsIds: [domainToRemove.rowId],
        elementsNames: [domainToRemove.displayName],
        title: 'policyPage.config.trustedDomainsContent.deleteDomain',
        notificationMsgLabelPath: 'common.wasDeletedSuccessfully',
        actionCallback: this.deleteDomains,
        warningBanner:
          'When a trusted domain is deleted, it may generate\nnew violations, potentially resulting in a substantial\namount of new violations.',
      } as IWarningDialogInput,
      disableClose: false,
      panelClass: 'sized-popup-dialog-container',
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((domainDeleted?: boolean) => {
        if (domainDeleted) {
          const index = this._domainsViewModel.indexOf(domainToRemove);
          if (index > -1) this._domainsViewModel.splice(index, 1);
          // remove from policy model
          const domainIndex = this.policyModel.config.allowedDomains.indexOf(domainToRemove.rowId);
          if (domainIndex > -1) this.policyModel.config.allowedDomains.splice(domainIndex, 1);
          this._updateDomainsViewModel();
          this.policyModel.config.allowedDomains = this._domainsViewModel.map((domain) => domain.rowId);
          this.policyUpdated.emit(this.policyModel);
        }
        this.resetSearchBoxBehavioralSubject$.next(this.allowedDomainsStartState);
      });
  }

  public exportDomainsToCSV() {
    this._policiesHttpService
      .getPolicy(this.integrationModel.id, this.policyModel.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (policy) => {
          if (!policy?.config?.allowedDomains) {
            this._notificationService.error('Policy data is invalid');
            return;
          }

          const domainsToExport = policy.config.allowedDomains;
          if (domainsToExport.length === 0) {
            this._notificationService.warning('No domains available to export');
            return;
          }

          const csvHeader = 'Trusted Domains';
          const csvContent = [csvHeader, ...domainsToExport].join('\n');

          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', 'trusted-domains.csv');
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          this._notificationService.success('Domains exported successfully');
        },
        error: (error) => {
          console.error('Error fetching domains for export:', error);
          this._notificationService.error('Failed to fetch domains for export');
          this._httpErrorHandlerService.handle(error);
        },
      });
  }

  private _updateDomainsViewModel() {
    // Sort domains alphabetically by displayName
    const sortedDomains = [...this._domainsViewModel].sort((a, b) =>
      a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase())
    );
    this.domainsCurrentPage$.next(sortedDomains);
    this.domainsForSearchBox = sortedDomains.map((domain) => domain.displayName);
  }

  private _transformToDomainViewModel(domain: string): IDomainsViewModel {
    return {
      rowId: domain,
      displayName: domain,
      clickable: false,
    };
  }

  private _updatePolicyConfig() {
    this.isLoading$.next(true);
    const domains = this._domainsViewModel.map((domainViewModel) => domainViewModel.rowId);

    // Calculate what changed
    const previousDomains = new Set(this.allowedDomainsStartState);
    const currentDomains = new Set(domains);
    const addedDomains = domains.filter((d) => !previousDomains.has(d));
    const removedDomains = this.allowedDomainsStartState.filter((d) => !currentDomains.has(d));

    combineLatest({
      accountSettingsUpdate: this._accountSettingsHttpService.addDomains(domains),
      policyUpdate: this._policiesHttpService.updatePolicy(
        this.integrationModel.id,
        this.policyModel.id,
        this._generateUpdatePolicyModel(domains)
      ),
    })
      .pipe(
        finalize(() => this.isLoading$.next(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          let message = 'Policy updated successfully.';
          if (addedDomains.length > 0) {
            message += ` ${addedDomains.length} new domain${addedDomains.length > 1 ? 's' : ''} added.`;
          }
          if (removedDomains.length > 0) {
            message += ` ${removedDomains.length} domain${removedDomains.length > 1 ? 's' : ''} removed.`;
          }

          this._notificationService.success(message);
          this._getPolicyUpdates();
        },
        error: (error) => {
          this._httpErrorHandlerService.handle(error);
        },
      });
  }

  private _getPolicyUpdates() {
    this._policiesHttpService
      .getPolicy(this.integrationModel.id, this.policyModel.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((policy) => {
        this.policyModel = policy;
        this.allowedDomainsStartState = this.policyModel.config.allowedDomains.slice();
        this._domainsViewModel = this.allowedDomainsStartState.map((domain) =>
          this._transformToDomainViewModel(domain)
        );
        this._updateDomainsViewModel();
        this.changesWereDone = false;

        this.notifyChanges.emit(this.changesWereDone);
        this.policyUpdated.emit(this.policyModel);
      });
  }

  private _generateUpdatePolicyModel(domains?: string[]): PolicyToUpdateModel {
    const config = _.cloneDeep(this.policyModel.config);
    config.allowedDomains = domains ? domains : this.allowedDomainsStartState;
    return {
      name: this.policyModel.name,
      description: this.policyModel.description,
      type: this.policyModel.isDefault ? undefined : this.policyModel.type,
      config: config,
    } as PolicyToUpdateModel;
  }
}
